import React, { useState, useEffect } from 'react';
import {
  Search,
  Check,
  X,
  Mail,
  Phone,
  Calendar,
  UserMinus,
  Trash2
} from 'lucide-react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';

const StatusBadge = ({ status, isDarkMode }) => {
  const colors = {
    pending: isDarkMode ? 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30' : 'bg-yellow-100 text-yellow-700 border-yellow-200',
    approved: isDarkMode ? 'bg-green-900/30 text-green-400 border-green-500/30' : 'bg-green-100 text-green-700 border-green-200',
    rejected: isDarkMode ? 'bg-red-900/30 text-red-400 border-red-500/30' : 'bg-red-100 text-red-700 border-red-200',
    revoked: isDarkMode ? 'bg-gray-800 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-300'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colors[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const AdminCard = ({ admin, onApprove, onReject, onRevoke, onDelete, isDarkMode }) => (
  <div className={`p-6 rounded-[24px] border transition-all duration-200 ${
    isDarkMode
      ? 'bg-[#111111] border-gray-800 hover:border-gray-700'
      : 'bg-white border-gray-100 hover:border-gray-200'
  }`}>
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className={`text-lg font-semibold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {admin.displayName}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <Mail className={`w-4 h-4 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`} />
          <span className={`text-sm ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {admin.email}
          </span>
        </div>
        {admin.phoneNumber && (
          <div className="flex items-center gap-2 mt-1">
            <Phone className={`w-4 h-4 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`} />
            <span className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {admin.phoneNumber}
            </span>
          </div>
        )}
      </div>
      <StatusBadge status={admin.status} isDarkMode={isDarkMode} />
    </div>

    <div className="flex items-center gap-2 mb-4">
      <Calendar className={`w-4 h-4 ${
        isDarkMode ? 'text-gray-500' : 'text-gray-400'
      }`} />
      <span className={`text-sm ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        Applied {new Date(admin.createdAt).toLocaleDateString()}
      </span>
    </div>

    <div className="flex gap-2">
      {admin.status === 'pending' && (
        <>
          <button
            onClick={() => onApprove(admin.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${
              isDarkMode
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <Check className="w-4 h-4" />
            Approve
          </button>
          <button
            onClick={() => onReject(admin.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${
              isDarkMode
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            <X className="w-4 h-4" />
            Reject
          </button>
        </>
      )}
      {admin.status === 'approved' && (
        <button
          onClick={() => onRevoke(admin.id)}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${
            isDarkMode
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          <UserMinus className="w-4 h-4" />
          Revoke
        </button>
      )}
      {(admin.status === 'rejected' || admin.status === 'revoked') && (
        <button
          onClick={() => onDelete(admin.id)}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${
            isDarkMode
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>
  </div>
);

const MosqueAdminManagement = () => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('pending');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadMosqueAdmins(activeTab === 'all' ? null : activeTab);
  }, [activeTab]);

  const loadMosqueAdmins = async (status) => {
    try {
      setLoading(true);
      const getAllMosqueAdmins = httpsCallable(functions, 'getAllMosqueAdmins');
      const result = await getAllMosqueAdmins({ status });
      setAdmins(result.data.admins);
    } catch (error) {
      console.error('Error loading mosque admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (adminId) => {
    if (!window.confirm('Are you sure you want to approve this mosque admin?')) return;

    try {
      const approveMosqueAdmin = httpsCallable(functions, 'approveMosqueAdmin');
      await approveMosqueAdmin({ adminId });
      alert('Mosque admin approved successfully!');
      loadMosqueAdmins(activeTab === 'all' ? null : activeTab);
    } catch (error) {
      console.error('Error approving admin:', error);
      alert('Failed to approve admin: ' + error.message);
    }
  };

  const handleReject = async (adminId) => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    if (reason === null) return; // User cancelled

    try {
      const rejectMosqueAdmin = httpsCallable(functions, 'rejectMosqueAdmin');
      await rejectMosqueAdmin({ adminId, reason });
      alert('Mosque admin rejected.');
      loadMosqueAdmins(activeTab === 'all' ? null : activeTab);
    } catch (error) {
      console.error('Error rejecting admin:', error);
      alert('Failed to reject admin: ' + error.message);
    }
  };

  const handleRevoke = async (adminId) => {
    if (!window.confirm('Revoke this mosque admin? They immediately lose all admin rights, including editing prayer times.')) return;
    const reason = window.prompt('Reason for revoking (optional, stored in the audit log):');

    try {
      const revokeMosqueAdmin = httpsCallable(functions, 'revokeMosqueAdmin');
      await revokeMosqueAdmin({ adminId, reason });
      alert('Mosque admin revoked.');
      loadMosqueAdmins(activeTab === 'all' ? null : activeTab);
    } catch (error) {
      console.error('Error revoking admin:', error);
      alert('Failed to revoke admin: ' + error.message);
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Permanently delete this mosque admin record? The user account itself is kept.')) return;

    try {
      const deleteMosqueAdmin = httpsCallable(functions, 'deleteMosqueAdmin');
      await deleteMosqueAdmin({ adminId });
      alert('Mosque admin deleted.');
      loadMosqueAdmins(activeTab === 'all' ? null : activeTab);
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Failed to delete admin: ' + error.message);
    }
  };

  const tabs = [
    { id: 'pending', label: 'Pending', count: admins.filter(a => a.status === 'pending').length },
    { id: 'approved', label: 'Approved', count: admins.filter(a => a.status === 'approved').length },
    { id: 'rejected', label: 'Rejected', count: admins.filter(a => a.status === 'rejected').length },
    { id: 'revoked', label: 'Revoked', count: admins.filter(a => a.status === 'revoked').length },
    { id: 'all', label: 'All', count: admins.length }
  ];

  const filteredAdmins = admins.filter(admin =>
    admin.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Mosque Administrators
        </h1>
        <p className={`mt-2 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Manage mosque admin applications and accounts
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
          isDarkMode ? 'text-gray-500' : 'text-gray-400'
        }`} />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-all ${
            isDarkMode
              ? 'bg-[#1A1A1A] border-gray-800 text-white placeholder-gray-500'
              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
          } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
        />
      </div>

      {/* Tabs */}
      <div className={`flex gap-2 p-1 rounded-xl ${
        isDarkMode ? 'bg-[#1A1A1A]' : 'bg-gray-100'
      }`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
              activeTab === tab.id
                ? isDarkMode
                  ? 'bg-emerald-500 text-black'
                  : 'bg-white text-gray-900 shadow-sm'
                : isDarkMode
                  ? 'text-gray-400 hover:text-white'
                  : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 ${
                activeTab === tab.id
                  ? isDarkMode ? 'text-black/60' : 'text-gray-500'
                  : isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Admin Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      ) : filteredAdmins.length === 0 ? (
        <div className={`text-center py-12 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          No mosque admins found
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredAdmins.map(admin => (
              <AdminCard
                key={admin.id}
                admin={admin}
                onApprove={handleApprove}
                onReject={handleReject}
                onRevoke={handleRevoke}
                onDelete={handleDelete}
                isDarkMode={isDarkMode}
              />
          ))}
        </div>
      )}
    </div>
  );
};

export default MosqueAdminManagement;
